import React, { useCallback, useRef, useState } from 'react';
import { Pencil, X } from 'lucide-react';
import { DSL_TAG_COLORS } from '../../../pages/dslAgentTest/colorPalette';
import type { DslTag, TagExecState } from '../../../pages/dslAgentTest/types';
import styles from './MonitorDashboard.module.css';
import { TraceEditPopover, type TraceEditPatch } from './TraceEditPopover';

export const MONITOR_DSL_DRAG_MIME = 'application/x-monitor-dsl-tag';

export interface MonitorDslDragPayload {
  sourceChartId: string;
  dsl: string;
}

type LegendStatus = 'loading' | 'ok' | 'error';

interface LegendStatusInfo {
  status: LegendStatus;
  label: string;
  className: string;
}

const statusInfo = (state: TagExecState | undefined): LegendStatusInfo => {
  if (!state || state.status === 'loading' || state.status === 'idle') {
    return { status: 'loading', label: 'loading', className: styles.legendStatusLoading };
  }
  if (state.status === 'error') {
    return { status: 'error', label: 'error', className: styles.legendStatusError };
  }
  return { status: 'ok', label: 'ok', className: styles.legendStatusOk };
};

export interface ChartLegendProps {
  sourceChartId: string;
  tags: DslTag[];
  execState: Record<string, TagExecState>;
  hiddenTagIds: ReadonlySet<string>;
  onToggleVisibility: (tagId: string) => void;
  onRemoveTag: (tagId: string) => void;
  onPatchTag: (tagId: string, patch: TraceEditPatch) => void;
}

export const ChartLegend: React.FC<ChartLegendProps> = ({
  sourceChartId,
  tags,
  execState,
  hiddenTagIds,
  onToggleVisibility,
  onRemoveTag,
  onPatchTag,
}) => {
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const pencilRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const setPencilRef = useCallback(
    (tagId: string) => (node: HTMLButtonElement | null) => {
      if (node) pencilRefs.current.set(tagId, node);
      else pencilRefs.current.delete(tagId);
    },
    [],
  );

  const handlePencilClick = useCallback((tagId: string) => {
    setEditingTagId((prev) => (prev === tagId ? null : tagId));
  }, []);

  const closePopover = useCallback(() => setEditingTagId(null), []);

  if (tags.length === 0) {
    return (
      <p className={styles.legendEmpty} data-testid="monitor-chart-legend-empty">
        Add a DSL above to start charting.
      </p>
    );
  }

  return (
    <ul
      className={styles.legend}
      role="list"
      data-testid="monitor-chart-legend"
    >
      {tags.map((tag) => {
        const info = statusInfo(execState[tag.id]);
        const isHidden = hiddenTagIds.has(tag.id);
        const toggleable = info.status === 'ok';
        const classes = [styles.legendRow, styles.legendRowDraggable];
        if (isHidden) classes.push(styles.legendRowHidden);

        return (
          <li
            key={tag.id}
            className={classes.join(' ')}
            role={toggleable ? 'button' : 'listitem'}
            tabIndex={toggleable ? 0 : undefined}
            aria-pressed={toggleable ? !isHidden : undefined}
            aria-label={
              toggleable
                ? `${isHidden ? 'Show' : 'Hide'} trace ${tag.dsl}`
                : tag.dsl
            }
            data-tag-id={tag.id}
            data-kind={info.status}
            data-hidden={isHidden ? 'true' : 'false'}
            data-testid="monitor-chart-legend-row"
            draggable
            onMouseDown={(event) => event.stopPropagation()}
            onTouchStart={(event) => event.stopPropagation()}
            onDragStart={(event) => {
              event.stopPropagation();
              const payload: MonitorDslDragPayload = {
                sourceChartId,
                dsl: tag.dsl,
              };
              const serialized = JSON.stringify(payload);
              event.dataTransfer.setData(MONITOR_DSL_DRAG_MIME, serialized);
              event.dataTransfer.setData('text/plain', tag.dsl);
              event.dataTransfer.effectAllowed = 'copy';
            }}
            onClick={toggleable ? () => onToggleVisibility(tag.id) : undefined}
            onKeyDown={
              toggleable
                ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onToggleVisibility(tag.id);
                    }
                  }
                : undefined
            }
          >
            <span
              className={styles.legendSwatch}
              style={{ backgroundColor: tag.color }}
              aria-hidden="true"
            />
            <span className={styles.legendDsl} title={tag.dsl}>
              {tag.dsl}
            </span>
            {info.status === 'ok' ? (
              <span aria-hidden="true" />
            ) : (
              <span className={`${styles.legendStatus} ${info.className}`}>
                {info.label}
              </span>
            )}
            <button
              ref={setPencilRef(tag.id)}
              type="button"
              className={styles.legendEditButton}
              onClick={(event) => {
                event.stopPropagation();
                handlePencilClick(tag.id);
              }}
              aria-label={`Edit ${tag.dsl}`}
              aria-pressed={editingTagId === tag.id}
              data-testid="monitor-chart-legend-edit"
            >
              <Pencil size={12} aria-hidden="true" />
            </button>
            <button
              type="button"
              className={styles.legendRemoveButton}
              onClick={(event) => {
                event.stopPropagation();
                onRemoveTag(tag.id);
              }}
              aria-label={`Remove ${tag.dsl} from chart`}
              data-testid="monitor-chart-legend-remove"
            >
              <X size={12} aria-hidden="true" />
            </button>
            {editingTagId === tag.id && (
              <TraceEditPopover
                anchorRef={{ current: pencilRefs.current.get(tag.id) ?? null }}
                tag={tag}
                palette={DSL_TAG_COLORS}
                onChange={(patch) => onPatchTag(tag.id, patch)}
                onClose={closePopover}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
};
